import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type {
  BoardPoint,
  HomeworkAnswerMode,
  HomeworkSubmissionReplayResponse,
  HomeworkTimeLimitSeconds,
} from '@wx-weiqi/contracts';

import type { HomeworkStore } from '../stores/homework-store';
import type { QuestionBankStore } from '../stores/question-bank-store';
import type { TeachingStore } from '../stores/teaching-store';
import {
  homeworkTracePathToStep,
  nextHomeworkTraceSteps,
  selectHomeworkTraceStep,
} from '../domain/homework-trace-navigation';
import {
  formatHomeworkElapsedTime,
  formatHomeworkSubmissionStatus,
} from '../domain/homework-review-presentation';
import { BoardSurface } from './BoardSurface';
import {
  canReviewHomeworkSubmission,
  latestHomeworkSubmissionForProblem,
} from '../domain/homework-grading';
import { buildQuestionBankTree } from './QuestionBankPanel';
import { QuestionBankDirectory } from './QuestionBankDirectory';

const TIME_OPTIONS: readonly {
  readonly value: HomeworkTimeLimitSeconds;
  readonly label: string;
}[] = [
  { value: 30, label: '30s' },
  { value: 45, label: '45s' },
  { value: 60, label: '1分钟' },
  { value: 120, label: '2分钟' },
  { value: 180, label: '3分钟' },
  { value: 300, label: '5分钟' },
];

const HOMEWORK_TRACE_COLORS = Object.freeze([
  '#d53939',
  '#2563d9',
  '#14845f',
  '#8b4bc1',
  '#e07a1f',
  '#00899a',
  '#d64f91',
  '#6f5a3d',
]);

export function HomeworkAssignmentComposer({
  homework,
  teaching,
  questionBank,
  onCancel,
  onCreated,
}: {
  readonly homework: HomeworkStore;
  readonly teaching: TeachingStore;
  readonly questionBank: QuestionBankStore;
  readonly onCancel: () => void;
  readonly onCreated: () => void;
}) {
  const homeworkState = useSyncExternalStore(homework.subscribe, homework.snapshot);
  const teachingState = useSyncExternalStore(teaching.subscribe, teaching.snapshot);
  const questionState = useSyncExternalStore(questionBank.subscribe, questionBank.snapshot);
  const [classId, setClassId] = useState('');
  const [title, setTitle] = useState('');
  const [timeLimitSeconds, setTimeLimitSeconds] = useState<HomeworkTimeLimitSeconds>(60);
  const [maxAttempts, setMaxAttempts] = useState<1 | 2 | 3>(1);
  const [answerMode, setAnswerMode] = useState<HomeworkAnswerMode>('MANUAL');
  const [studentIds, setStudentIds] = useState<readonly string[]>([]);
  const [problemIds, setProblemIds] = useState<readonly string[]>([]);
  const [problemPickerOpen, setProblemPickerOpen] = useState(false);
  const [showProblemPreviews, setShowProblemPreviews] = useState(true);

  useEffect(() => {
    void Promise.all([teaching.loadTeacherDashboard(), questionBank.loadCatalog()]).catch(
      () => undefined,
    );
  }, [questionBank, teaching]);

  useEffect(() => {
    if (classId !== '' || teachingState.classes.length === 0) return;
    setClassId(teachingState.classes[0]?.classId ?? '');
  }, [classId, teachingState.classes]);

  useEffect(() => {
    const available = new Set(
      (teachingState.membersByClass[classId] ?? []).map((member) => member.membership.membershipId),
    );
    setStudentIds((current) => current.filter((studentId) => available.has(studentId)));
  }, [classId, teachingState.membersByClass]);

  const students = teachingState.membersByClass[classId] ?? [];
  const selectedPoint = questionState.categories
    .flatMap((category) => category.knowledgePoints)
    .find((point) => point.knowledgePointId === questionState.selectedKnowledgePointId);
  const questionTree = useMemo(
    () => buildQuestionBankTree(questionState.categories),
    [questionState.categories],
  );
  const busy = homeworkState.pending !== null || teachingState.pending !== null;
  const questionBusy = questionState.pending !== null;

  const toggleProblem = (problemId: string) => {
    setProblemIds((current) =>
      current.includes(problemId)
        ? current.filter((candidate) => candidate !== problemId)
        : [...current, problemId],
    );
  };

  const toggleStudent = (studentId: string) => {
    setStudentIds((current) =>
      current.includes(studentId)
        ? current.filter((candidate) => candidate !== studentId)
        : [...current, studentId],
    );
  };

  const submit = () => {
    if (
      title.trim().length === 0 ||
      classId === '' ||
      problemIds.length === 0 ||
      studentIds.length === 0
    ) {
      return;
    }
    void homework
      .createAndPublish({
        classId,
        title,
        questionBankProblemIds: problemIds,
        timeLimitSeconds,
        maxAttempts: answerMode === 'MANUAL' ? 1 : maxAttempts,
        answerMode,
        studentMembershipIds: studentIds,
      })
      .then(onCreated)
      .catch(() => undefined);
  };

  return (
    <div className="homework-compose">
      <div className="homework-form-grid">
        <label className="homework-field homework-field--source">
          <span>题目来源</span>
          <button
            className={
              problemIds.length > 0
                ? 'homework-source-button is-complete'
                : 'homework-source-button'
            }
            type="button"
            onClick={() => setProblemPickerOpen(true)}
          >
            {problemIds.length > 0 ? `已添加 ${problemIds.length} 题 ✅` : '从题库选取题目'}
          </button>
        </label>
        <fieldset className="homework-field">
          <legend>每题用时</legend>
          <div className="homework-choice-row">
            {TIME_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={timeLimitSeconds === option.value ? 'is-selected' : ''}
                aria-pressed={timeLimitSeconds === option.value}
                onClick={() => setTimeLimitSeconds(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>
        <label className="homework-field">
          <span>作业名称</span>
          <input
            maxLength={160}
            value={title}
            placeholder="例如：周末死活练习"
            onChange={(event) => setTitle(event.target.value)}
          />
        </label>
        <fieldset className="homework-field">
          <legend>作答次数</legend>
          <div className="homework-choice-row">
            {([1, 2, 3] as const).map((attempts) => (
              <button
                key={attempts}
                type="button"
                disabled={answerMode === 'MANUAL'}
                aria-pressed={(answerMode === 'MANUAL' ? 1 : maxAttempts) === attempts}
                className={
                  (answerMode === 'MANUAL' ? 1 : maxAttempts) === attempts ? 'is-selected' : ''
                }
                onClick={() => setMaxAttempts(attempts)}
              >
                {attempts}次
              </button>
            ))}
          </div>
          {answerMode === 'MANUAL' ? <small>手动模式固定为 1 次</small> : null}
        </fieldset>
        <fieldset className="homework-field homework-mode-field">
          <legend>作答形式</legend>
          <button
            type="button"
            className={answerMode === 'MANUAL' ? 'is-selected' : ''}
            aria-pressed={answerMode === 'MANUAL'}
            onClick={() => {
              setAnswerMode('MANUAL');
              setMaxAttempts(1);
            }}
          >
            <strong>手动</strong>
            <small>学生摆出完整变化，提交后由教师批改</small>
          </button>
          <button
            type="button"
            className={answerMode === 'AUTOMATIC' ? 'is-selected' : ''}
            aria-pressed={answerMode === 'AUTOMATIC'}
            onClick={() => setAnswerMode('AUTOMATIC')}
          >
            <strong>自动</strong>
            <small>按 SGF 分支逐手应对，走完即判对，分支外即判错</small>
          </button>
        </fieldset>
        <div className="homework-field" role="group" aria-label="添加学生">
          <span>添加学生</span>
          <select
            aria-label="学生所在班级"
            value={classId}
            onChange={(event) => setClassId(event.target.value)}
          >
            {teachingState.classes.map((managedClass) => (
              <option key={managedClass.classId} value={managedClass.classId}>
                {managedClass.name}
              </option>
            ))}
          </select>
          <div className="homework-student-list">
            <button
              type="button"
              aria-pressed={students.length > 0 && studentIds.length === students.length}
              className={
                students.length > 0 && studentIds.length === students.length ? 'is-selected' : ''
              }
              onClick={() =>
                setStudentIds(
                  studentIds.length === students.length
                    ? []
                    : students.map((student) => student.membership.membershipId),
                )
              }
            >
              全选
            </button>
            {students.map((student) => {
              const id = student.membership.membershipId;
              return (
                <button
                  key={id}
                  type="button"
                  aria-pressed={studentIds.includes(id)}
                  className={studentIds.includes(id) ? 'is-selected' : ''}
                  onClick={() => toggleStudent(id)}
                >
                  {student.membership.user.displayName}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {homeworkState.error !== null ? (
        <p className="message message--error">{homeworkState.error}</p>
      ) : null}
      <footer className="homework-form-actions">
        <button type="button" onClick={onCancel} disabled={busy}>
          取消
        </button>
        <button
          className="primary-button"
          type="button"
          disabled={
            busy || title.trim().length === 0 || problemIds.length === 0 || studentIds.length === 0
          }
          onClick={submit}
        >
          {homeworkState.pending === 'create-homework' ? '正在发送…' : '确定'}
        </button>
      </footer>

      {problemPickerOpen ? (
        <div className="nested-dialog-backdrop">
          <section
            className="problem-picker problem-picker--question-library"
            role="dialog"
            aria-modal="true"
            aria-label="从题库选题"
          >
            <header>
              <div>
                <p className="eyebrow">题目来源</p>
                <h3>从题库选取题目</h3>
              </div>
              <button
                type="button"
                className="dialog-close"
                onClick={() => setProblemPickerOpen(false)}
              >
                ×
              </button>
            </header>
            <div className="problem-picker-messages" aria-live="polite">
              {questionState.error !== null ? (
                <p className="message message--error">{questionState.error}</p>
              ) : null}
            </div>
            <section className="question-library-view question-library-view--directory problem-picker-library-view">
              <QuestionBankDirectory
                sections={questionTree}
                selectedPointId={questionState.selectedKnowledgePointId}
                loading={questionState.pending === 'load-catalog'}
                onSelect={(pointId) =>
                  void questionBank.selectKnowledgePoint(pointId, 1).catch(() => undefined)
                }
              />

              <div className="question-library-content">
                <div
                  className={`question-grid${showProblemPreviews ? ' question-grid--preview' : ''}`}
                >
                  {questionState.problems.map((problem) => {
                    const selected = problemIds.includes(problem.problemId);
                    return (
                      <article
                        className={`question-card problem-picker-card${selected ? ' is-selected' : ''}`}
                        key={problem.problemId}
                      >
                        <div className="question-card-preview">
                          {showProblemPreviews ? (
                            <BoardSurface
                              size={problem.boardSize}
                              stones={problem.stones}
                              initialAnnotations={problem.initialAnnotations ?? []}
                              lastMove={null}
                            />
                          ) : (
                            <span>预览图已收起</span>
                          )}
                        </div>
                        <strong title={problem.filename}>{problem.filename}</strong>
                        <small>
                          {problem.boardSize} 路 ·{' '}
                          {problem.currentPlayer === 'BLACK' ? '黑先' : '白先'}
                        </small>
                        <button
                          type="button"
                          className={selected ? 'is-selected' : ''}
                          aria-pressed={selected}
                          onClick={() => toggleProblem(problem.problemId)}
                        >
                          {selected ? '已选择 ✓' : '选择题目'}
                        </button>
                      </article>
                    );
                  })}
                  {questionState.problems.length === 0 && !questionBusy ? (
                    <div className="question-bank-empty question-bank-empty--grid">
                      <strong>
                        {selectedPoint === undefined ? '尚未选择目录' : '该目录暂无题目'}
                      </strong>
                      <span>
                        {selectedPoint === undefined
                          ? '请从左侧选择知识点。'
                          : '请在题库管理中向该目录导入题目。'}
                      </span>
                    </div>
                  ) : null}
                </div>
                <footer className="question-library-footer problem-picker-footer">
                  <nav aria-label="题目分页">
                    <button
                      type="button"
                      disabled={
                        questionBusy ||
                        questionState.problemPage <= 1 ||
                        questionState.selectedKnowledgePointId === null
                      }
                      onClick={() =>
                        questionState.selectedKnowledgePointId === null
                          ? undefined
                          : void questionBank
                              .selectKnowledgePoint(
                                questionState.selectedKnowledgePointId,
                                questionState.problemPage - 1,
                              )
                              .catch(() => undefined)
                      }
                    >
                      上一页
                    </button>
                    <span>
                      第 {questionState.problemPage} / {questionState.problemPageCount} 页
                    </span>
                    <button
                      type="button"
                      disabled={
                        questionBusy ||
                        questionState.problemPage >= questionState.problemPageCount ||
                        questionState.selectedKnowledgePointId === null
                      }
                      onClick={() =>
                        questionState.selectedKnowledgePointId === null
                          ? undefined
                          : void questionBank
                              .selectKnowledgePoint(
                                questionState.selectedKnowledgePointId,
                                questionState.problemPage + 1,
                              )
                              .catch(() => undefined)
                      }
                    >
                      下一页
                    </button>
                  </nav>
                  <div>
                    <span className="problem-picker-selected-count">
                      已选 {problemIds.length} 题
                    </span>
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => setShowProblemPreviews((current) => !current)}
                    >
                      {showProblemPreviews ? '收起预览图' : '生成预览图'}
                    </button>
                    <button
                      className="secondary-button"
                      type="button"
                      disabled={problemIds.length === 0}
                      onClick={() => setProblemIds([])}
                    >
                      清空选择
                    </button>
                    <button
                      className="primary-button"
                      type="button"
                      disabled={problemIds.length === 0}
                      onClick={() => setProblemPickerOpen(false)}
                    >
                      确定题目
                    </button>
                  </div>
                </footer>
              </div>
            </section>
          </section>
        </div>
      ) : null}
    </div>
  );
}

export function HomeworkReviewPanel({
  homework,
  teaching,
  onCancel,
}: {
  readonly homework: HomeworkStore;
  readonly teaching: TeachingStore;
  readonly onCancel: () => void;
}) {
  const homeworkState = useSyncExternalStore(homework.subscribe, homework.snapshot);
  const teachingState = useSyncExternalStore(teaching.subscribe, teaching.snapshot);
  const [classId, setClassId] = useState('');
  const [selectedHomeworkId, setSelectedHomeworkId] = useState('');
  const [answerReplay, setAnswerReplay] = useState<HomeworkSubmissionReplayResponse['data'] | null>(
    null,
  );
  const [answerReplayIndex, setAnswerReplayIndex] = useState(0);
  const [replayMode, setReplayMode] = useState<'answer' | 'trace'>('answer');
  const [tracePath, setTracePath] = useState<readonly number[]>([]);
  const [traceCursor, setTraceCursor] = useState(0);

  useEffect(() => {
    void teaching.loadTeacherDashboard().catch(() => undefined);
  }, [teaching]);

  useEffect(() => {
    const next = classId || teachingState.classes[0]?.classId;
    if (next === undefined || next === '') return;
    if (next !== classId) setClassId(next);
    void homework.loadClassHomeworks(next).catch(() => undefined);
  }, [classId, homework, teachingState.classes]);

  const studentNames = useMemo(
    () =>
      new Map(
        teachingState.students.map(
          (student) => [student.membershipId, student.user.displayName] as const,
        ),
      ),
    [teachingState.students],
  );
  const currentTraceStepIndex = traceCursor === 0 ? null : (tracePath[traceCursor - 1] ?? null);
  const currentTraceStep =
    currentTraceStepIndex === null ? undefined : answerReplay?.trace.steps[currentTraceStepIndex];
  const initialTraceBoard = answerReplay?.frames[0]?.board;
  const currentTraceBoard = currentTraceStep?.board ?? initialTraceBoard;
  const nextTraceSteps = useMemo(
    () => nextHomeworkTraceSteps(answerReplay?.trace.steps ?? [], currentTraceStepIndex),
    [answerReplay, currentTraceStepIndex],
  );
  const traceChoiceMarkers = useMemo(
    () =>
      nextTraceSteps.flatMap((step) =>
        step.move.kind === 'PLAY'
          ? [
              {
                point: step.move.point,
                color:
                  HOMEWORK_TRACE_COLORS[(step.branchIndex - 1) % HOMEWORK_TRACE_COLORS.length]!,
                label: String(step.moveNumber),
              },
            ]
          : [],
      ),
    [nextTraceSteps],
  );
  const traceStoneLabels = useMemo(() => {
    if (answerReplay === null || currentTraceBoard === undefined) return [];
    const labels = new Map<
      string,
      {
        readonly point: BoardPoint;
        readonly label: string;
        readonly color: 'BLACK' | 'WHITE';
      }
    >();
    tracePath.slice(0, traceCursor).forEach((stepIndex) => {
      const step = answerReplay.trace.steps[stepIndex];
      if (step?.move.kind !== 'PLAY') return;
      const parentBoard =
        step.parentStepIndex === null
          ? initialTraceBoard
          : answerReplay.trace.steps[step.parentStepIndex]?.board;
      const color = parentBoard?.currentPlayer;
      if (
        color === undefined ||
        currentTraceBoard.stones[step.move.point.y * currentTraceBoard.size + step.move.point.x] !==
          color
      ) {
        return;
      }
      labels.set(`${step.move.point.x}:${step.move.point.y}`, {
        point: step.move.point,
        label: String(step.moveNumber),
        color,
      });
    });
    return [...labels.values()];
  }, [answerReplay, currentTraceBoard, initialTraceBoard, traceCursor, tracePath]);
  const traceBranches = useMemo(() => {
    const counts = new Map<number, number>();
    answerReplay?.trace.steps.forEach((step) => {
      counts.set(step.branchIndex, (counts.get(step.branchIndex) ?? 0) + 1);
    });
    return [...counts.entries()].sort(([left], [right]) => left - right);
  }, [answerReplay]);

  const openReplay = (submissionId: string, mode: 'answer' | 'trace') => {
    void homework
      .loadSubmissionReplay(submissionId)
      .then((replay) => {
        setAnswerReplay(replay);
        setAnswerReplayIndex(replay.frames.length - 1);
        setReplayMode(mode);
        setTracePath(homeworkTracePathToStep(replay.trace.steps, replay.trace.submittedStepIndex));
        setTraceCursor(0);
      })
      .catch(() => undefined);
  };

  const chooseTraceStep = (stepIndex: number) => {
    if (answerReplay === null) return;
    const selection = selectHomeworkTraceStep(
      answerReplay.trace.steps,
      tracePath,
      traceCursor,
      stepIndex,
    );
    if (selection === null) return;
    setTracePath(selection.path);
    setTraceCursor(selection.cursor);
  };

  const chooseTracePoint = (point: BoardPoint) => {
    const step = nextTraceSteps.find(
      (candidate) =>
        candidate.move.kind === 'PLAY' &&
        candidate.move.point.x === point.x &&
        candidate.move.point.y === point.y,
    );
    if (step !== undefined) chooseTraceStep(step.stepIndex);
  };

  return (
    <div className="homework-review">
      <aside>
        <label>
          班级
          <select value={classId} onChange={(event) => setClassId(event.target.value)}>
            {teachingState.classes.map((managedClass) => (
              <option key={managedClass.classId} value={managedClass.classId}>
                {managedClass.name}
              </option>
            ))}
          </select>
        </label>
        <div className="homework-review-list">
          {homeworkState.homeworks.map((item) => (
            <button
              key={item.homeworkId}
              type="button"
              className={selectedHomeworkId === item.homeworkId ? 'is-selected' : ''}
              onClick={() => {
                setSelectedHomeworkId(item.homeworkId);
                void homework.loadHomeworkAssignments(item.homeworkId).catch(() => undefined);
              }}
            >
              <strong>{item.title}</strong>
              <small>
                {item.items.length} 题 · {item.status === 'PUBLISHED' ? '进行中' : '已结束'}
              </small>
            </button>
          ))}
        </div>
        <button className="return-button" type="button" onClick={onCancel}>
          返回
        </button>
      </aside>
      <section className="homework-review-content">
        {selectedHomeworkId === '' ? (
          <div className="empty-panel">
            <strong>选择一份作业开始批改</strong>
            <span>学生提交后会按账号集中展示。</span>
          </div>
        ) : homeworkState.assignments.length === 0 ? (
          <div className="empty-panel">
            <strong>暂无学生提交</strong>
            <span>作业已发出，等待学生完成。</span>
          </div>
        ) : (
          homeworkState.assignments.map((assignment) => (
            <article key={assignment.assignmentId} className="homework-student-submission">
              <header>
                <div>
                  <strong>{studentNames.get(assignment.studentMembershipId) ?? '学生'}</strong>
                  <small>
                    {assignment.status === 'REVIEWED'
                      ? '已批改'
                      : assignment.status === 'SUBMITTED'
                        ? '待批改'
                        : '作答中'}
                  </small>
                </div>
                <span>
                  {assignment.submissions.length} / {assignment.items.length} 题已提交
                </span>
              </header>
              <div className="homework-submission-grid">
                {assignment.submissions.map((submission) => (
                  <div className="homework-submission-card" key={submission.submissionId}>
                    <div className="homework-submission-card__heading">
                      <strong>
                        第{' '}
                        {assignment.items.findIndex(
                          (item) => item.problem.problemId === submission.problemId,
                        ) + 1}{' '}
                        题
                      </strong>
                      <time dateTime={`PT${Math.round((submission.elapsedMs ?? 0) / 1_000)}S`}>
                        {formatHomeworkElapsedTime(submission.elapsedMs)}
                      </time>
                    </div>
                    <span>
                      {submission.answer.length} 手 ·{' '}
                      {formatHomeworkSubmissionStatus(submission.status)}
                    </span>
                    <div className="homework-submission-view-actions">
                      <button
                        type="button"
                        disabled={homeworkState.pending !== null}
                        onClick={() => openReplay(submission.submissionId, 'answer')}
                      >
                        查看学生作答
                      </button>
                      <button
                        type="button"
                        disabled={homeworkState.pending !== null}
                        onClick={() => openReplay(submission.submissionId, 'trace')}
                      >
                        查看落子轨迹
                      </button>
                    </div>
                    {canReviewHomeworkSubmission(submission) &&
                    latestHomeworkSubmissionForProblem(assignment, submission.problemId)
                      ?.submissionId === submission.submissionId ? (
                      <div className="homework-submission-grade-actions">
                        <button
                          type="button"
                          aria-label="判为错误"
                          disabled={homeworkState.pending !== null}
                          onClick={() =>
                            void homework
                              .reviewSubmission(
                                submission.submissionId,
                                submission.submissionVersion,
                                'INCORRECT',
                                0,
                                submission.feedback ?? '',
                              )
                              .catch(() => undefined)
                          }
                        >
                          ❌
                        </button>
                        <button
                          type="button"
                          className="primary-button"
                          aria-label="判为正确"
                          disabled={homeworkState.pending !== null}
                          onClick={() =>
                            void homework
                              .reviewSubmission(
                                submission.submissionId,
                                submission.submissionVersion,
                                'CORRECT',
                                100,
                                submission.feedback ?? '',
                              )
                              .catch(() => undefined)
                          }
                        >
                          ✅
                        </button>
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            </article>
          ))
        )}
        {homeworkState.error !== null ? (
          <p className="message message--error">{homeworkState.error}</p>
        ) : null}
      </section>
      {answerReplay !== null ? (
        <div className="nested-dialog-backdrop homework-replay-backdrop">
          <section
            className="homework-replay-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="查看学生作答记录"
          >
            <header>
              <div>
                <p className="eyebrow">{replayMode === 'answer' ? '学生作答' : '完整探索记录'}</p>
                <h3>
                  {replayMode === 'answer'
                    ? `第 ${answerReplayIndex} 手`
                    : currentTraceStep === undefined
                      ? `请选择第 1 手（${nextTraceSteps.length} 个选点）`
                      : nextTraceSteps.length === 0
                        ? `第 ${currentTraceStep.moveNumber} 手 · 本分支结束`
                        : `第 ${currentTraceStep.moveNumber} 手 · 请选择第 ${currentTraceStep.moveNumber + 1} 手`}
                </h3>
              </div>
              <div className="homework-replay-mode-switch" aria-label="作答记录查看方式">
                <button
                  type="button"
                  aria-pressed={replayMode === 'answer'}
                  onClick={() => setReplayMode('answer')}
                >
                  查看学生作答
                </button>
                <button
                  type="button"
                  aria-pressed={replayMode === 'trace'}
                  onClick={() => setReplayMode('trace')}
                >
                  查看落子轨迹
                </button>
              </div>
              <button className="dialog-close" type="button" onClick={() => setAnswerReplay(null)}>
                ×
              </button>
            </header>
            <div className="homework-replay-board">
              <BoardSurface
                size={
                  replayMode === 'trace'
                    ? (currentTraceBoard?.size ?? 19)
                    : (answerReplay.frames[answerReplayIndex]?.board.size ?? 19)
                }
                stones={
                  replayMode === 'trace'
                    ? (currentTraceBoard?.stones ?? Array.from({ length: 361 }, () => null))
                    : (answerReplay.frames[answerReplayIndex]?.board.stones ??
                      Array.from({ length: 361 }, () => null))
                }
                lastMove={
                  replayMode === 'answer' &&
                  answerReplay.frames[answerReplayIndex]?.move?.kind === 'PLAY'
                    ? answerReplay.frames[answerReplayIndex].move.point
                    : null
                }
                stoneLabels={replayMode === 'trace' ? traceStoneLabels : []}
                choiceMarkers={replayMode === 'trace' ? traceChoiceMarkers : []}
                {...(replayMode === 'trace'
                  ? {
                      isActionable: (point: BoardPoint) =>
                        nextTraceSteps.some(
                          (step) =>
                            step.move.kind === 'PLAY' &&
                            step.move.point.x === point.x &&
                            step.move.point.y === point.y,
                        ),
                      onPoint: chooseTracePoint,
                    }
                  : {})}
              />
            </div>
            {replayMode === 'answer' ? (
              <footer>
                <button
                  type="button"
                  disabled={answerReplayIndex <= 0}
                  onClick={() => setAnswerReplayIndex((current) => Math.max(0, current - 1))}
                >
                  后退
                </button>
                <span>
                  {answerReplayIndex} / {answerReplay.frames.length - 1}
                </span>
                <button
                  type="button"
                  disabled={answerReplayIndex >= answerReplay.frames.length - 1}
                  onClick={() =>
                    setAnswerReplayIndex((current) =>
                      Math.min(answerReplay.frames.length - 1, current + 1),
                    )
                  }
                >
                  前进
                </button>
              </footer>
            ) : (
              <footer className="homework-trace-footer">
                <div className="homework-trace-controls">
                  <button
                    type="button"
                    disabled={traceCursor <= 0}
                    onClick={() => setTraceCursor((current) => Math.max(0, current - 1))}
                  >
                    后退
                  </button>
                  <span>
                    当前第 {currentTraceStep?.moveNumber ?? 0} 手
                    {nextTraceSteps.length > 0
                      ? ` · 显示 ${nextTraceSteps.length} 个下一手选点`
                      : ' · 分支已结束'}
                  </span>
                  <button
                    type="button"
                    disabled={traceCursor >= tracePath.length}
                    onClick={() =>
                      setTraceCursor((current) => Math.min(tracePath.length, current + 1))
                    }
                  >
                    前进
                  </button>
                </div>
                {nextTraceSteps.some((step) => step.move.kind === 'PASS') ? (
                  <div className="homework-trace-pass-choices">
                    {nextTraceSteps
                      .filter((step) => step.move.kind === 'PASS')
                      .map((step) => (
                        <button
                          key={step.stepIndex}
                          type="button"
                          onClick={() => chooseTraceStep(step.stepIndex)}
                        >
                          第 {step.moveNumber} 手：停一手
                        </button>
                      ))}
                  </div>
                ) : null}
                <div className="homework-trace-legend">
                  {traceBranches.map(([branchIndex, moveCount]) => (
                    <span key={branchIndex}>
                      <i
                        style={{
                          background:
                            HOMEWORK_TRACE_COLORS[(branchIndex - 1) % HOMEWORK_TRACE_COLORS.length],
                        }}
                      />
                      分支 {branchIndex} · {moveCount} 手
                    </span>
                  ))}
                </div>
              </footer>
            )}
          </section>
        </div>
      ) : null}
    </div>
  );
}
